import React from 'react';
import { ArrowRight, Timer, BarChart2, Lock } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/app/id6751061434';

const WhyDifferent = () => {
  const points = [
    {
      icon: <Timer size={20} />,
      title: 'A real countdown — not a gentle nudge.',
      description: 'Every commitment has a deadline that ticks down in real time. You see the seconds disappear. That pressure is the point.'
    },
    {
      icon: <BarChart2 size={20} />,
      title: 'Your data is transparent.',
      description: "Every task you commit to is recorded — finished, failed, or abandoned. You can't hide from your own pattern."
    },
    {
      icon: <Lock size={20} />,
      title: 'Strict Mode locks you in.',
      description: "Enable it, and the app blocks navigation during your timer. No switching to Instagram. Just you and the task."
    }
  ];

  return (
    <section className="relative py-32 px-6 lg:px-12" style={{ background: '#08080f' }}>
      <div className="max-w-5xl mx-auto relative z-10">

        {/* Section Label */}
        <div className="text-center mb-16 reveal">
          <div className="section-label">The Difference</div>
          <h2 className="text-4xl lg:text-6xl font-black tracking-tight mb-6">
            Other apps remind you.
            <br />
            <span className="gradient-text">TickBoom records you.</span>
          </h2>
          <p className="text-lg text-[rgba(255,255,255,0.45)] max-w-xl mx-auto">
            There's a difference between tracking tasks and being held accountable to them.
          </p>
        </div>

        {/* Comparison block */}
        <div className="cyber-card p-8 lg:p-10 mb-16 reveal delay-200">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Other apps */}
            <div>
              <div className="text-xs font-mono font-medium uppercase tracking-widest text-[#6b7280] mb-5">Every other app</div>
              <div className="space-y-4">
                {[
                  '"No worries! Snooze it for tomorrow. 😊"',
                  '"You\'re doing great — just postpone it."',
                  '"Task moved to next week. Keep it up!"'
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-[rgba(255,255,255,0.2)] mt-0.5 flex-shrink-0">✗</span>
                    <p className="text-[rgba(255,255,255,0.3)] italic text-sm">{line}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-[rgba(255,255,255,0.06)]" style={{ position: 'absolute' }}></div>

            {/* TickBoom */}
            <div>
              <div className="text-xs font-mono font-medium uppercase tracking-widest gradient-text mb-5">TickBoom</div>
              <div className="space-y-4">
                {[
                  '"Task abandoned. Stats recorded. Data doesn\'t lie."',
                  '"25-min commitment started. Clock is running."',
                  '"87% success rate this week. Keep the streak."'
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="gradient-text mt-0.5 flex-shrink-0">✓</span>
                    <p className="text-white font-medium text-sm">{line}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 3 Key Points */}
        <div className="grid md:grid-cols-3 gap-10 mb-16">
          {points.map((point, index) => (
            <div key={index}>
              <div className="w-9 h-9 mb-4 flex items-center justify-center rounded-lg bg-[rgba(124,58,237,0.1)] text-[#7C3AED]">
                {point.icon}
              </div>
              <h3 className="text-base font-semibold mb-2 text-white">{point.title}</h3>
              <p className="text-[rgba(255,255,255,0.45)] text-sm leading-relaxed">{point.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Try TickBoom Free
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyDifferent;
